import { Feather } from '@expo/vector-icons';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import React, { useCallback, useEffect, useState } from 'react';
import { FlatList, useWindowDimensions } from 'react-native';
import styled from 'styled-components/native';
import ActivityIndicatorScreen from '../../components/ActivityIndicatorScreen';
import Button from '../../components/Button';
import DynamicTime from '../../components/DynamicTime';
import IconBadge from '../../components/IconBadge';
import ListItem from '../../components/list/ListItem';
import ListItemAccessory from '../../components/list/ListItemAccessory';
import ListItemText from '../../components/list/ListItemText';
import { Caption } from '../../components/Typography';
import Colors from '../../constants/Colors';
import { useUserContext } from '../../context/UserContext';
import { useGetRoomList } from '../../hooks/room/useGetRoomList';
import { usePubSubNewUserMessage } from '../../hooks/session/usePubSubNewUserMessage';
import { MessengerParamList } from '../../navigation/MessengerNav';
import Link from '../../components/Link';
import elementIDs from '../../../elementIDs';

const StyledView = styled.View`
  flex: 1;
  background-color: white;
`;

const FixedHeightListItem = styled(ListItem)`
  height: 96px;
`;

const StyledTopSpacerView = styled.View``;

const StyledAccessoryView = styled.View`
  position: absolute;
  top: 18px;
  right: 16px;
  justify-content: center;
` as React.FC;

const StyledChevron = styled(ListItemAccessory)`
  position: relative;
  padding-top: 20px;
  padding-right: 20px;
`;

const ConversationStarterText = styled(Caption)`
  color: ${Colors.primary};
`;

const StyledButton = styled(Button)`
  position: absolute;
  bottom: 13px;
  right: 16px;
  width: 50%;
`;

const StyledLink = styled(Link)`
  align-self: flex-end;
  padding: 10px 14px;
`;

const MessengerRoomListScreen: React.FC = () => {
  const { user } = useUserContext();
  const { roomList, loadingRooms, getRooms } = useGetRoomList();
  const [includeArchived, setIncludeArchived] = useState<boolean>(false);
  // refresh the room list on focus
  useFocusEffect(
    useCallback(() => {
      getRooms({ includeArchived });
    }, [getRooms, includeArchived])
  );
  // update room list when new messages arrive
  const { refreshToken } = usePubSubNewUserMessage(
    user?.id || 0,
    'MessengerRoomListScreen',
    true
  );
  useEffect(() => {
    if (refreshToken) getRooms({ includeArchived });
  }, [getRooms, refreshToken, includeArchived]);

  const navigation = useNavigation<StackNavigationProp<MessengerParamList>>();
  const window = useWindowDimensions();
  return (
    <StyledView>
      <StyledTopSpacerView></StyledTopSpacerView>
      <StyledLink
        regular
        onPress={() => setIncludeArchived(v => !v)}
        inverse
      >{`${includeArchived ? 'Hide' : 'Show'} Archived`}</StyledLink>
      <FlatList
        data={roomList?.data || []}
        keyExtractor={item => item.id.toString()}
        renderItem={({ item }) => (
          <FixedHeightListItem
            testID={`${elementIDs.chatListItemID}-${item.name}`}
            divider='extended'
            onPress={() => {
              navigation.navigate('MessengerRoom', {
                roomId: item.id
              });
            }}
          >
            {/* <ListItemIcon>
              <ProfileButtonMenu name={item.name} />
            </ListItemIcon> */}
            <ListItemText primary={item.name} />
            <StyledChevron>
              {item.unread > 0 ? (
                <IconBadge value={item.unread} top={8} />
              ) : (
                <Feather
                  name='chevron-right'
                  size={20}
                  color={Colors.secondaryBlack}
                />
              )}
            </StyledChevron>
            <StyledAccessoryView>
              <DynamicTime time={item.modified} />
            </StyledAccessoryView>
          </FixedHeightListItem>
        )}
      />
      {/* <StyledButton
        startIcon={<Feather name='edit-2' size={20} color='white' />}
        onPress={() => navigation.navigate('MessengerRoom')}
      >
        New Message
      </StyledButton> */}
      {loadingRooms && <ActivityIndicatorScreen modal />}
    </StyledView>
  );
};

export default MessengerRoomListScreen;
